import React from 'react';
import { Link } from 'react-router';
import {
  STUDIO_COPY,
  STUDIO_FREE_FEATURES,
  STUDIO_PRICING,
  STUDIO_PRO_FEATURES,
  isStudioReelPacksEnabled,
  type StudioCheckoutPlan,
} from '../config';

type StudioSubPlan = Extract<StudioCheckoutPlan, 'monthly' | 'annual'>;

type PlanCardsProps = {
  selected: StudioSubPlan;
  onSelect: (plan: StudioSubPlan) => void;
  billingEnabled: boolean;
  isLoggedOut: boolean;
  submitting?: boolean;
  submittingPack?: boolean;
  onUpgrade: () => void;
  onPackCheckout?: () => void;
};

/**
 * Pricing catalog — Free + Pro, equal height. Cadence is a toggle, not a third card.
 */
const PlanCards: React.FC<PlanCardsProps> = ({
  selected,
  onSelect,
  billingEnabled,
  isLoggedOut,
  submitting = false,
  submittingPack = false,
  onUpgrade,
  onPackCheckout,
}) => {
  const showAnnual = STUDIO_PRICING.showAnnual;
  const packsEnabled = isStudioReelPacksEnabled();
  const showPack = billingEnabled;
  const packLive = billingEnabled && packsEnabled;
  const proPrice =
    selected === 'annual'
      ? STUDIO_PRICING.annual.priceLabel
      : STUDIO_PRICING.monthly.priceLabel;
  const freeHref = isLoggedOut ? '/studio/connect' : '/studio/vods';
  const proDisabled = !billingEnabled || submitting;
  const proLabel = !billingEnabled
    ? STUDIO_COPY.pricingComingSoon
    : submitting
      ? STUDIO_COPY.checkoutPending
      : STUDIO_COPY.pricingProCta;

  return (
    <div className="studio-section" data-testid="studio-plan-cards">
      {showAnnual ? (
        <div
          className="studio-plan-toggle"
          role="radiogroup"
          aria-label="Studio Pro plan"
        >
          <button
            type="button"
            role="radio"
            aria-checked={selected === 'monthly'}
            className={selected === 'monthly' ? 'is-active' : ''}
            onClick={() => onSelect('monthly')}
            data-testid="studio-plan-monthly"
          >
            {STUDIO_COPY.pricingToggleMonthly}
            <span className="sr-only"> {STUDIO_PRICING.monthly.priceLabel}</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={selected === 'annual'}
            className={selected === 'annual' ? 'is-active' : ''}
            onClick={() => onSelect('annual')}
            data-testid="studio-plan-annual"
          >
            {STUDIO_COPY.pricingToggleAnnual}
            <span className="sr-only"> {STUDIO_PRICING.annual.priceLabel}</span>
            <span className="ml-2 text-xs uppercase tracking-wide text-[var(--studio-accent-bright)]">
              {STUDIO_PRICING.annual.saveLabel}
            </span>
          </button>
        </div>
      ) : null}

      <div className="studio-card-grid studio-card-grid--2">
        <article className="studio-card studio-plan-card" data-testid="studio-plan-free">
          <h2 className="studio-card-title">{STUDIO_COPY.pricingFreeName}</h2>
          <p className="studio-plan-card-price">{STUDIO_PRICING.free.label}</p>
          <ul className="studio-plan-card-features">
            {STUDIO_FREE_FEATURES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Link
            to={freeHref}
            className="studio-btn-ghost no-underline studio-plan-card-cta"
            data-testid="studio-plan-free-cta"
          >
            {STUDIO_COPY.pricingFreeCta}
          </Link>
        </article>

        <article
          className="studio-card studio-plan-card is-featured"
          data-testid="studio-plan-pro"
        >
          <h2 className="studio-card-title">{STUDIO_COPY.pricingProName}</h2>
          <p className="studio-plan-card-price">{proPrice}</p>
          <ul className="studio-plan-card-features">
            {STUDIO_PRO_FEATURES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {isLoggedOut && billingEnabled ? (
            <Link
              to="/studio/connect"
              className="studio-btn-primary no-underline studio-plan-card-cta"
              data-testid="studio-upgrade-cta"
            >
              {STUDIO_COPY.pricingProCta}
            </Link>
          ) : (
            <button
              type="button"
              className="studio-btn-primary studio-plan-card-cta"
              onClick={() => onUpgrade()}
              disabled={proDisabled}
              data-testid="studio-upgrade-cta"
            >
              {proLabel}
            </button>
          )}
        </article>
      </div>

      {showPack ? (
        <article
          className="studio-card studio-plan-card mt-6"
          data-testid="studio-plan-pack"
        >
          <h2 className="studio-card-title">{STUDIO_COPY.pricingPackName}</h2>
          <p className="studio-plan-card-price">
            {STUDIO_PRICING.pack5.priceLabel}{' '}
            <span className="text-sm font-normal text-[var(--studio-muted)]">
              one-time
            </span>
          </p>
          <p className="studio-plan-card-features">{STUDIO_COPY.pricingPackValue}</p>
          <p className="text-sm text-[var(--studio-muted)]">
            {STUDIO_COPY.pricingPackBestValueHint}
          </p>
          {isLoggedOut && packLive ? (
            <Link
              to="/studio/connect"
              className="studio-btn-ghost no-underline studio-plan-card-cta"
              data-testid="studio-pack-cta"
            >
              {STUDIO_COPY.pricingPackCta}
            </Link>
          ) : (
            <button
              type="button"
              className="studio-btn-ghost studio-plan-card-cta"
              onClick={() => onPackCheckout?.()}
              disabled={!packLive || submitting || submittingPack || !onPackCheckout}
              data-testid="studio-pack-cta"
            >
              {!packsEnabled
                ? STUDIO_COPY.pricingComingSoon
                : submittingPack
                  ? STUDIO_COPY.checkoutPending
                  : STUDIO_COPY.pricingPackCta}
            </button>
          )}
        </article>
      ) : null}
    </div>
  );
};

export default PlanCards;
